import { Visual } from '@/modules/Visual/Index';
import { cn } from '@/shared/utils/cn';
import { VisualSlotType, IconFontColor, IconFontSize } from '@/shared/types/types';
import React from 'react';

export interface ListItemDefaultProps {
  children: React.ReactNode;
  icon?: VisualSlotType;
  /** Leading icon size (default `sm`). Trailing chevrons are rendered by the caller (e.g. `SidebarMenuItem` uses `xs` on the right only). */
  iconSize?: IconFontSize;
  iconColor?: IconFontColor;
  // Duotone props for icon
  iconDuotonePrimary?: IconFontColor;
  iconDuotoneSecondary?: IconFontColor;
  iconDuotoneOpacityPrimary?: number;
  iconDuotoneOpacitySecondary?: number;
  disabled?: boolean;
  current?: boolean;
  color?: 'blue' | 'red';
  onClick?: (e: React.MouseEvent) => void;
  className?: string;
}

export const ListItemDefault: React.FC<ListItemDefaultProps> = ({
  children,
  icon,
  iconSize = 'sm',
  iconColor,
  iconDuotonePrimary,
  iconDuotoneSecondary,
  iconDuotoneOpacityPrimary,
  iconDuotoneOpacitySecondary,
  disabled = false,
  current = false,
  color = 'blue',
  onClick,
  className,
}) => {
  const handleClick = (e: React.MouseEvent) => {
    if (disabled) return;
    onClick?.(e);
  };

  const baseClasses =
    'ui-group ui-flex ui-items-center ui-gap-x-8 ui-p-8 ui-rounded-control-sm ui-transition-all ui-duration-300 ui-text-body-sm-regular ui-font-body-sm-regular ui-min-h-32 ui-leading-body-sm-regular ui-tracking-body-sm';

  const stateClasses = cn({
    'ui-opacity-70 ui-cursor-not-allowed ui-select-none': disabled,
    'ui-cursor-pointer': !disabled,
    // Selected/Current: azul oscuro con texto blanco
    'ui-bg-fill-brand ui-text-on-color': current && color === 'blue',
    'ui-bg-surface-danger ui-text-danger': color === 'red',
    // Default text
    'ui-text-primary': !current && color === 'blue',
    // Hover: gris claro
    'hover:ui-bg-fill-hover': !disabled && !current && color === 'blue',
    'hover:ui-bg-surface-danger-hover': !disabled && color === 'red',
  });

  // Icon color classes using Tailwind group-hover
  const iconWrapperClasses = cn(
    'ui-transition-colors ui-duration-300 ui-flex ui-items-center ui-justify-center ui-flex-shrink-0 ui-w-16 ui-h-16',
    '[transform:translateZ(0)]', // Crear capa de composición separada para evitar movimiento durante animaciones
    {
      // Default colors based on variant
      'ui-text-secondary': color === 'blue' && !iconColor && !current,
      'ui-text-on-color': current && color === 'blue' && !iconColor,
      // Danger row (`color="red"`) or explicit danger icon (e.g. logout): keep icon wrapper on danger token
      'ui-text-danger': (color === 'red' && !iconColor) || iconColor === 'danger',
    }
  );

  // Determinar el color del icono: usar el proporcionado, o 'secondary' por defecto, o 'white' si está seleccionado
  const finalIconColor: IconFontColor =
    iconColor || (current && color === 'blue' ? 'white' : 'secondary');

  return (
    <li className={cn(baseClasses, stateClasses, className)} onClick={handleClick}>
      {icon && (
        <span className={iconWrapperClasses}>
          <Visual
            slot={icon}
            colorIcon={finalIconColor}
            size={iconSize}
            // defaultSizeIcon={false}
            fallback={children?.toString().substring(0, 1)}
            colorIconDuotonePrimary={iconDuotonePrimary}
            colorIconDuotoneSecondary={iconDuotoneSecondary}
            opacityIconDuotonePrimary={iconDuotoneOpacityPrimary}
            opacityIconDuotoneSecondary={iconDuotoneOpacitySecondary}
          />
        </span>
      )}
      {children}
    </li>
  );
};
